import { useCallback, useState } from 'react';

import { ArrowRightIcon, InfoIcon } from 'lucide-react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import * as yup from 'yup';

import Link from '@/components/atoms/Link/Link';
import { WhatsAppTemplateCombobox } from '@/components/atoms/WhatsAppTemplateCombobox';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { useCloseModal } from '@/hooks/useCloseModal';
import useMessages from '@/hooks/useMessages';
import ScheduleTimeFields from '@/modules/humanChat/components/Conversation/MessageArea/ScheduledMessages/ScheduleTimeFields';
import {
  buildScheduledAt,
  getNextHalfHourSchedule,
  getTemplateBodyPreview,
  isValidScheduleDelay,
} from '@/modules/humanChat/components/Conversation/MessageArea/ScheduledMessages/utils';
import { useScheduledMessages } from '@/modules/humanChat/hooks/useScheduledMessages';
import { TemplateMessageMode } from '@/modules/humanChat/hooks/useTemplateMessage';
import { WHATSAPP_MANAGER_URL } from '@/modules/whatsapp/constants';
import { useWhatsAppTemplates } from '@/modules/whatsapp/hooks/useWhatsAppTemplates';
import {
  filterMessageWhatsappTemplate,
  formatComponentsFromForm,
  getComponentsFromTemplate,
  getVariablesLength,
  startVariablesFromIndex,
} from '@/modules/whatsapp/utils';
import { selectSessionId } from '@/redux/session/selectors';
import { BroadcastAction } from '@/shared/types/broadcast';
import { Message } from '@/shared/types/chat';
import { getUserTimezone } from '@/util/dateUtils';

import { TemplateVariables } from './TemplateVariables';

import styles from './ModalTemplateMessage.module.scss';

interface Props {
  integration_id: string;
  desk_id: string;
  mode?: TemplateMessageMode;
  onPrimaryButtonClick: () => void;
}

const _formSchema = yup.object({
  template: yup.string().min(1),
  headerParameters: yup.array(yup.object({ name: yup.string().nullable() })),
  bodyParameters: yup.array(yup.object({ name: yup.string().nullable() })),
});

type Form = yup.InferType<typeof _formSchema>;

/** Date + "HH:mm" + IANA timezone, as picked on the second step. */
interface Schedule {
  date: Date;
  time: string;
  timezone: string;
}

// Find this in human chat, in an whatsapp conversation over 24h long
export const ModalTemplateMessage = ({
  desk_id,
  integration_id,
  mode = 'schedule',
  onPrimaryButtonClick,
}: Props) => {
  const { t } = useTranslation();
  const currentSessionId = useSelector(selectSessionId);
  // Pinned at open: desk/integration ids arrive as snapshots in props, and the
  // POST must target the conversation the modal was opened from even if the
  // agent navigates (browser back/forward) while the wizard is up.
  const [sessionId] = useState(currentSessionId);
  const closeModal = useCloseModal();

  const { createMessage, createStatus } = useMessages(desk_id, sessionId);
  const { createScheduledMessage, createStatus: scheduleStatus } = useScheduledMessages(
    desk_id,
    sessionId
  );

  // 'schedule' turns this into a wizard: step 0 picks the template, step 1 the
  // send time. 'send' keeps the single-step immediate-send modal.
  const isScheduling = mode === 'schedule';
  const [step, setStep] = useState<0 | 1>(0);
  const [schedule, setSchedule] = useState<Schedule | null>(null);

  const formMethods = useForm<Form>({ defaultValues: { template: '' } });
  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { isValid },
  } = formMethods;

  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const templateId = useWatch({ control, name: 'template' });

  const { append: headerAppend } = useFieldArray({ control, name: 'headerParameters' });

  const { append: bodyAppend } = useFieldArray({ control, name: 'bodyParameters' });

  const { templates: whatsAppTemplates, status: listwhatsAppTemplatesStatus } =
    useWhatsAppTemplates({ deskId: desk_id, integrationId: integration_id });

  /**
   * The selected template with the form's variable values baked into its
   * components. `null` when the selection cannot be resolved — the templates may
   * still be loading, or the template may have been deleted on the Meta side.
   */
  const resolveTemplate = (formData: Form): BroadcastAction | null => {
    const template = whatsAppTemplates?.find((t) => t.id === formData.template);
    if (!template) {
      return null;
    }
    // formatComponentsFromForm only uses headerParameters and bodyParameters from formData
    const components = formatComponentsFromForm(
      template,
      formData as unknown as Parameters<typeof formatComponentsFromForm>[1]
    );
    return { ...template, components } as BroadcastAction;
  };

  const scheduledAt = schedule
    ? buildScheduledAt(schedule.date, schedule.time, schedule.timezone)
    : null;
  const isScheduleValid = scheduledAt !== null && isValidScheduleDelay(scheduledAt);

  // Step 0 → step 1. The default send time is computed here rather than on mount
  // so it can't go stale while the agent picks a template.
  const onNext = handleSubmit(() => {
    setSchedule((prev) => {
      // Preserve a deliberate pick across Previous → Next, but recompute one that
      // lapsed while the agent was back on the template step — otherwise step 1
      // reopens on a past time with Schedule disabled.
      if (prev && isValidScheduleDelay(buildScheduledAt(prev.date, prev.time, prev.timezone))) {
        return prev;
      }
      return { ...getNextHalfHourSchedule(), timezone: getUserTimezone() };
    });
    setStep(1);
  });

  const onSchedule = handleSubmit((formData) => {
    if (scheduleStatus === 'pending' || scheduledAt === null) {
      return;
    }
    const template = resolveTemplate(formData);
    if (!template) {
      return;
    }
    // Revalidate against the current clock — `isScheduleValid` is a render-time
    // snapshot, and the modal may have sat open past the picked time.
    if (!isValidScheduleDelay(scheduledAt)) {
      toast.error(t('scheduled_messages.delay_hint'));
      return;
    }
    // The success toast lives in the hook's mutation-scope onSuccess so it
    // survives this modal unmounting mid-POST; this callback only closes it.
    createScheduledMessage(
      { template, scheduled_at: scheduledAt },
      { onSuccess: onPrimaryButtonClick }
    );
  });

  // Immediate send — the >24h WhatsApp reply flow reached from the window-closed
  // "Browse templates" card ('send' mode).
  const onSend = handleSubmit((formData) => {
    // A double-clicked send is a duplicate template, charged twice.
    if (createStatus === 'pending') {
      return;
    }
    const template = resolveTemplate(formData);
    if (!template) {
      return;
    }
    const text = getTemplateBodyPreview(template) || template.name;
    const message = { body: { text, template } } as unknown as Message;

    createMessage(message, {
      onSuccess: () => {
        onPrimaryButtonClick();
      },
    });
  });

  const onDateChange = useCallback(
    (date: Date) => setSchedule((prev) => (prev ? { ...prev, date } : prev)),
    []
  );
  const onTimeChange = useCallback(
    (time: string) => setSchedule((prev) => (prev ? { ...prev, time } : prev)),
    []
  );
  const onTimezoneChange = useCallback(
    (timezone: string) => setSchedule((prev) => (prev ? { ...prev, timezone } : prev)),
    []
  );

  const template = whatsAppTemplates?.find((t) => t.id === templateId);
  const { header, body, footer, buttons } = getComponentsFromTemplate(template);

  const handleTemplateChange = (newTemplateId: string) => {
    if (newTemplateId !== templateId) {
      setValue('headerParameters', []);
      setValue('bodyParameters', []);
      clearErrors('headerParameters');
      clearErrors('bodyParameters');

      const newTemplate = whatsAppTemplates?.find((t) => t.id === newTemplateId);
      const { header, body } = getComponentsFromTemplate(newTemplate);
      const headerVariablesCount = getVariablesLength(header);
      const bodyVariablesCount = getVariablesLength(body);

      for (let i = 0; i < headerVariablesCount; i++) {
        headerAppend({ name: null });
      }
      for (let i = 0; i < bodyVariablesCount; i++) {
        bodyAppend({ name: null });
      }
    }
  };

  const headerVariablesCount = getVariablesLength(header);
  const bodyVariablesCount = getVariablesLength(body);

  const selectableTemplates = filterMessageWhatsappTemplate(whatsAppTemplates) ?? [];

  const variableCount = headerVariablesCount + bodyVariablesCount;

  const chargesNote = (
    <span className="mr-auto flex items-center gap-2 text-sm leading-5 font-normal text-muted-foreground">
      <InfoIcon size={16} className="text-muted-foreground" />
      {t('chatBox.incur_charges')}
    </span>
  );

  // Early returns instead of nested ternaries: immediate send, then the two steps.
  const renderFooter = () => {
    if (!isScheduling) {
      return (
        <DialogFooter>
          {chargesNote}
          <Button variant="ghost-neutral" onClick={closeModal}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={onSend}
            disabled={!isValid}
            isLoading={createStatus === 'pending' || listwhatsAppTemplatesStatus === 'pending'}
          >
            {t('common.send')}
          </Button>
        </DialogFooter>
      );
    }

    if (step === 0) {
      return (
        <DialogFooter>
          {chargesNote}
          <Button variant="ghost-neutral" onClick={closeModal}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onNext} disabled={!isValid}>
            {t('common.next')}
            <ArrowRightIcon />
          </Button>
        </DialogFooter>
      );
    }

    // No charges note here — the template step already stated it, and on this
    // narrower step it wraps to two lines and squeezes the buttons.
    return (
      <DialogFooter>
        <Button variant="ghost-neutral" onClick={() => setStep(0)}>
          {t('common.previous')}
        </Button>
        <Button
          onClick={onSchedule}
          disabled={!isScheduleValid}
          isLoading={scheduleStatus === 'pending'}
        >
          {t('scheduled_messages.schedule_message')}
        </Button>
      </DialogFooter>
    );
  };

  // Kept mounted-or-not as a whole: react-hook-form retains the values while the
  // time step is showing, so Previous comes back to the same selection.
  const templateStep = (
    <>
      <Form {...formMethods}>
        <FormField
          control={control}
          name="template"
          rules={{ required: true }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('broadcasts.templates')}</FormLabel>
              <WhatsAppTemplateCombobox
                templates={selectableTemplates}
                selectedTemplateId={field.value}
                isLoading={listwhatsAppTemplatesStatus === 'pending'}
                disabled={
                  listwhatsAppTemplatesStatus !== 'pending' && selectableTemplates.length === 0
                }
                open={templatePickerOpen}
                onOpenChange={setTemplatePickerOpen}
                placeholder={t('brains.select_a_template')}
                onSelect={(id) => {
                  if (id) {
                    field.onChange(id);
                    handleTemplateChange(id);
                  }
                }}
              />
            </FormItem>
          )}
        />
      </Form>
      {templateId && (
        <div className={styles.templateContainer}>
          {header?.text && (
            <div>
              <span className="text-sm leading-5 font-semibold">
                #{t('broadcasts.template_header')}#
              </span>
              <p className="text-sm leading-5 font-normal">{header.text}</p>
            </div>
          )}
          {body?.text && (
            <div>
              <span className="text-sm leading-5 font-semibold">#{t('dialog.email.body')}#</span>
              <p className="text-sm leading-5 font-normal">
                {startVariablesFromIndex(headerVariablesCount, body.text)}
              </p>
            </div>
          )}
          {footer?.text && (
            <div>
              <span className="text-sm leading-5 font-semibold">#{t('broadcasts.footer')}#</span>
              <p className="text-sm leading-5 font-normal">{footer.text}</p>
            </div>
          )}

          {buttons && (
            <div>
              <span className="text-sm leading-5 font-semibold">
                #{t('dialog.carousel.buttons')}#
              </span>
              <p className="text-sm leading-5 font-normal">
                {buttons.map((button, index) => `[${index}] ${button.text} `)}
              </p>
            </div>
          )}
        </div>
      )}
      {variableCount > 0 && templateId && (
        <TemplateVariables<Form>
          key={templateId}
          headerVariablesCount={headerVariablesCount}
          bodyVariablesCount={bodyVariablesCount}
          control={control}
          template={template}
        />
      )}
    </>
  );

  return (
    <Dialog open onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className={step === 1 ? 'sm:max-w-lg' : 'sm:max-w-2xl'}>
        <DialogHeader>
          <DialogTitle>
            {isScheduling ? t('scheduled_messages.schedule_message') : t('broadcasts.templates')}
          </DialogTitle>
        </DialogHeader>
        {/* Where to find the templates only matters when this modal is the
            immediate-send flow; the wizard title already says what it does. */}
        {!isScheduling && (
          <p className="mb-6 text-sm leading-5 font-normal">
            <Trans
              i18nKey="broadcasts.select_template"
              components={{ link1: <Link href={WHATSAPP_MANAGER_URL} external /> }}
            />
          </p>
        )}
        {isScheduling && schedule && step === 1 ? (
          <ScheduleTimeFields
            date={schedule.date}
            time={schedule.time}
            timezone={schedule.timezone}
            onDateChange={onDateChange}
            onTimeChange={onTimeChange}
            onTimezoneChange={onTimezoneChange}
          />
        ) : (
          templateStep
        )}
        {renderFooter()}
      </DialogContent>
    </Dialog>
  );
};
